import { Link, useSearchParams } from 'react-router-dom';
import { Enum_Legaldocument_Legaldocumenttype } from 'graphql/generated/strapi';
import { ReactComponent as CodexIcon } from 'assets/icons/codexLogo.svg';
import { FooterTypes } from './footer.types';
import { useGetLegalDocuments } from 'hooks/legalDocuments/useGetLegalDocuments';
import { pageIds, SUPPORT_EMAIL } from 'utilities/constants';
import { useGetPage } from 'hooks/useGetPage';
import { twMerge } from 'tailwind-merge';
import { LEGAL_DOCUMENTS_ROUTE } from 'utilities/routes';
import COPPAAgreementModal from '../navbar/NavbarProfileDropdownComponents/COPPAAgreementModal';
import React, { useMemo } from 'react';
import { useAuth } from 'auth/context/AuthContext';
import { Roles } from '../../firebase/interfaces';
import { Hasura_Legal_Document_Types_Enum } from 'graphql/generated/hasura';
import { convertType } from '../../const/notification';

interface FooterProps {
  type?: FooterTypes;
  closeNavbarMobile?: () => void;
}

const COPPA_AGREEMENT_MODAL_QUERY = 'coppa-agreement';

const containerSizeClasses =
  'w-full min-h-[425px] h-auto desktop:min-h-fit py-6 desktop:py-10';
const containerPositionClasses =
  'flex flex-col desktop:flex-row desktop:justify-between items-start desktop:items-center';
const textClasses = 'text-sm text-center font-semibold';
const linkClasses =
  'text-base desktop:text-link font-semibold desktop:font-semibold underline';

const Footer: React.FC<FooterProps> = ({
  type = 'default',
  closeNavbarMobile,
}) => {
  const { legalDocuments } = useGetLegalDocuments({
    locale: 'en',
    legalDocumentTypes: [
      Enum_Legaldocument_Legaldocumenttype.TermsOfService,
      Enum_Legaldocument_Legaldocumenttype.PrivacyPolicy,
      Enum_Legaldocument_Legaldocumenttype.BaaAgreement,
      Enum_Legaldocument_Legaldocumenttype.HippaAgreement,
      Enum_Legaldocument_Legaldocumenttype.NoticeOfPrivacy,
      Enum_Legaldocument_Legaldocumenttype.CancellationPolicy,
    ],
  });

  const { user } = useAuth();

  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.FOOTER_COMPONENT,
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const handleClickCoppa = () => {
    searchParams.set(COPPA_AGREEMENT_MODAL_QUERY, 'true');
    setSearchParams(searchParams);
  };

  const isCOPPAOpen = searchParams.get(COPPA_AGREEMENT_MODAL_QUERY) === 'true';

  const showDocuments = useMemo(() => {
    return user?.role === Roles.PROVIDER
      ? [
          Hasura_Legal_Document_Types_Enum.BaaAgreement,
          Hasura_Legal_Document_Types_Enum.HippaAgreement,
          Hasura_Legal_Document_Types_Enum.TermsOfService,
          Hasura_Legal_Document_Types_Enum.PrivacyPolicy,
          Hasura_Legal_Document_Types_Enum.CancellationPolicy,
        ]
      : [
          Hasura_Legal_Document_Types_Enum.TermsOfService,
          Hasura_Legal_Document_Types_Enum.PrivacyPolicy,
          Hasura_Legal_Document_Types_Enum.CancellationPolicy,
          Hasura_Legal_Document_Types_Enum.NoticeOfPrivacy,
        ];
  }, [user?.role]);

  let backgroundColor: string,
    textColor: string,
    borderClass: string,
    iconClass: string,
    contact: string;

  switch (type) {
    case 'login':
      backgroundColor = 'bg-transparent';
      textColor = 'text-white';
      borderClass = '';
      iconClass = 'stroke-white';
      contact = 'text-white';
      break;
    case 'signup':
      backgroundColor = 'bg-base-content';
      textColor = 'text-med-gray-3';
      borderClass = '';
      iconClass = 'stroke-black';
      contact = 'text-clc-blue';
      break;
    case 'navbar':
      backgroundColor = 'bg-transparent';
      textColor = 'text-med-gray-3';
      borderClass = '';
      iconClass = 'stroke-clc-black';
      contact = 'text-clc-blue';
      break;
    default:
      backgroundColor = 'bg-footer';
      textColor = 'text-med-gray-3';
      borderClass = 'desktop:border-t';
      iconClass = 'stroke-black';
      contact = 'text-clc-blue';
  }

  if (loading && !locale) return null;

  return (
    <>
      {isCOPPAOpen && <COPPAAgreementModal isEditState={false} />}

      <div
        className={`${containerSizeClasses} ${containerPositionClasses} ${backgroundColor} ${textColor} ${borderClass} desktop:px-[135px] desktop:grid grid-cols-12 desktop:gap-2.5 shrink-0`}
      >
        <div className="flex flex-col col-span-5 text-sm font-semibold desktop:gap-y-4 w-full desktop:mr-1 desktop:ml-0">
          <div
            className={twMerge(
              'flex max-desktop:hidden whitespace-nowrap justify-start flex-row items-center',
              textClasses,
            )}
          >
            <span className="mr-1">
              {locale?.needHelp ?? 'Need help? Please'}
            </span>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className={twMerge('underline', contact)}
            >
              {locale?.contactUs ?? 'Contact Us'}
            </a>
          </div>
          <div className="flex flex-col desktop:flex-row items-center justify-center desktop:justify-start w-auto mx-auto desktop:mx-0">
            <CodexIcon
              className={`${iconClass} h-20 w-20 desktop:h-10 desktop:w-10 desktop:mr-4`}
            />
            <p className={`${textClasses} ${textColor} mt-4 desktop:mt-0`}>
              {locale?.copyrightText}
              <span className="desktop:hidden">
                <br />
              </span>{' '}
              {locale?.allRightsText}
            </p>
          </div>
        </div>
        <div
          className={twMerge(
            'flex flex-col desktop:hidden whitespace-nowrap justify-center w-auto desktop:min-w-[350px] mx-auto max-desktop:mt-10 desktop:flex-row items-center',
            textClasses,
          )}
        >
          <span className="mr-1">
            {locale?.needHelp ?? 'Need help? Please'}
          </span>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className={twMerge('underline', contact)}
          >
            {locale?.contactUs ?? 'Contact Us'}
          </a>
        </div>
        <div className="flex col-span-7 flex-row flex-wrap justify-center desktop:justify-end gap-x-5 gap-y-3 items-center mx-auto desktop:mr-0 mt-8 desktop:mt-0 px-4 desktop:px-0">
          {user?.role === Roles.PATIENT && (
            <span
              onClick={handleClickCoppa}
              className={`${linkClasses} ${textColor} desktop:mt-0 text-sm cursor-pointer`}
            >
              {locale?.coppaAgreement || 'COPPA Agreement'}
            </span>
          )}
          {showDocuments?.map((documentType) => {
            const document = legalDocuments?.find((doc) => {
              return convertType[doc.legalDocumentType] === documentType;
            });

            if (!document) return null;

            return (
              <Link
                target="_blank"
                key={document.legalDocumentId}
                to={`${LEGAL_DOCUMENTS_ROUTE}/${documentType}`}
                className={`${linkClasses} ${textColor} desktop:mt-0 text-sm`}
                onClick={closeNavbarMobile}
              >
                {document.legalDocumentName}
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default Footer;
